import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { EmailLink, TextLink } from '@/components/ui/TextLink';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { CONTACT_NAME_LABELS } from '@/lib/contact-name';
import { cx } from '@/lib/cx';
import { formatDateTime, orDash, pluralise } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import type { Contact, ContactGroup, ContactListSort, ListOrder, Paginated } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useTableFilters, type ColumnSpec, type TableFilters } from '@/lib/use-table-filters';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { fetchAllPages, useShowAll } from '@/lib/use-show-all';
import { ContactFormModal } from '@/components/contacts/ContactFormModal';
import {
  Avatar,
  Badge,
  Button,
  ColumnManager,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterableTH,
  FilterSheet,
  Input,
  ListPager,
  PageHeader,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  THead,
  TR,
  type ColumnFilterOption,
  type FilterableTHProps,
  type FrozenSide,
} from '@/components/ui';

const PER_PAGE = 10;
/** Fewer than a full page: enough to read as a list, not a wall of shimmer. */
const SKELETON_ROWS = 6;

/**
 * ⛔ **THE ONE ORDERED COLUMN LIST. Header, body and skeleton all render from
 * it, and none of them holds a list of its own.**
 *
 * Before the column manager there were THREE hand-written lists of these cells —
 * the `<thead>`, the row body, and `TableSkeleton` — and the only thing keeping
 * them 1:1 was a comment in each asking the next person to remember. That is the
 * failure this file already records at the `pinned` header note: *"It is pinned
 * in THREE places… miss the skeleton and the column jumps sideways when the data
 * lands."* A column added here now appears in all three or in none.
 *
 * ⚠️ **DECLARED order, not rendered order.** `useColumnLayout` treats this as
 * the authority for which columns EXIST and where a column the reader has never
 * reordered belongs; the stored layout only ever permutes and subtracts from it.
 *
 * `department` sits beside `company` because the two are how somebody narrows to
 * a person they need: which school, then which department. A reader may now move
 * it; this is where it starts.
 */
const COLUMN_KEYS = [
  'name',
  /* The two name parts, beside Full name (owner, 2026-10-16). Page-scoped
     funnels only: `/contacts` neither filters nor sorts on them. */
  'firstName',
  'lastName',
  'title',
  'company',
  'department',
  'createdAt',
  'updatedAt',
  'actions',
] as const;

type ContactColumnKey = (typeof COLUMN_KEYS)[number];

function isColumnKey(key: string): key is ContactColumnKey {
  return (COLUMN_KEYS as readonly string[]).includes(key);
}

const ACTIONS_KEY = 'actions';
const ACTIONS_LABEL = 'Actions';

/**
 * ⛔ **Actions is LOCKED: it can be neither hidden nor moved.** It is the only
 * route to Edit and Delete on this table — the sub-`md` card list has its own
 * buttons, but a value reachable only below a breakpoint is indistinguishable
 * from one that was removed, and `/companies` has already produced a report of
 * the row menu being "deleted" when it merely became unreachable. A hidden
 * actions lane would be that report again, this time with a preference behind it
 * that survives a reload.
 *
 * The manager still LISTS it, disabled with a `title` saying why, so its absence
 * cannot read as the feature having lost the column.
 */
const LOCKED_KEYS = [ACTIONS_KEY] as const;

/**
 * Keys this table renders with `pinned` — Actions, and only ever that.
 *
 * ⚠️ **NOT A SECOND NAME FOR `LOCKED_KEYS`, though they hold the same one key
 * here.** Locked answers *may the reader hide or move this*; pinned answers
 * *which edge does this cell already occupy*. Most list pages lock their
 * identifying column too — and that column is the best freeze-LEFT candidate
 * there is, so conflating the two takes the feature's main use away. Two
 * constants, so a key added to one cannot join the other by accident.
 *
 * Passed to both hooks, each for its own reason: `useColumnLayout` so a freeze
 * that would fight `sticky right-0` cannot be stored (it rendered as a pressed
 * button that moved nothing), and `useFrozenOffsets` so the lane's WIDTH is
 * counted into the right-edge sum.
 */
const PINNED_KEYS = [ACTIONS_KEY] as const;

/**
 * Why Actions cannot be hidden, in this table's own terms.
 *
 * ⚠️ **The shared fallback says only "This column cannot be hidden" — the rule,
 * not the reason.** Here the reason is the argument: Edit and Delete exist
 * nowhere else above `md`, and the sub-`md` card list is a different breakpoint,
 * not a second home. A **why-this-is-disabled** sentence is one of the four
 * kinds the no-helper-text prohibition explicitly keeps, because a bare
 * `disabled` makes a locked column and a broken switch identical.
 */
function lockReasonOf(key: string): string | undefined {
  return key === ACTIONS_KEY ? 'Edit and Delete live only in this column' : undefined;
}

/** The page slug, matching the saved-filter scopes (`companies`, `deals`, …). */
const COLUMN_LAYOUT_SCOPE = 'contacts';

interface ContactColumnMeta {
  /**
   * The header's width utility. ⚠️ **It and `minWidth` are ONE decision written
   * twice** — Tailwind needs a literal class and the table needs a number — so
   * they move together or the table's floor stops describing its columns.
   */
  headClass?: string;
  /**
   * This column's share of the table's minimum width, in px.
   *
   * ⚠️ **`name` is 60 and that is not a typo.** The identity column carries no
   * width utility — it takes whatever the other six leave — so its share of the
   * FLOOR is its own content minimum (avatar, a truncated name, `px-3` twice),
   * not the ~320px it usually renders at. 60 is the residual this file's own
   * arithmetic has used through three widths: 1020 with an Owner column, 844
   * once it went on 2026-08-31, and 1004 once Department arrived. Summed over
   * all seven keys it still comes to **1004**, so a table with nothing hidden is
   * byte-identical to the `min-w-[1004px]` this replaced. First and Last name
   * (2026-10-16) add 144 each, so the full nine-column floor is 1292.
   */
  minWidth: number;
}

const COLUMN_META: Record<ContactColumnKey, ContactColumnMeta> = {
  name: { minWidth: 60 },
  firstName: { headClass: 'w-36', minWidth: 144 },
  lastName: { headClass: 'w-36', minWidth: 144 },
  title: { headClass: 'w-40', minWidth: 160 },
  company: { headClass: 'w-48', minWidth: 192 },
  department: { headClass: 'w-40', minWidth: 160 },
  createdAt: { headClass: 'w-40', minWidth: 160 },
  updatedAt: { headClass: 'w-44', minWidth: 176 },
  actions: { headClass: 'w-24', minWidth: 96 },
};

/** Body-cell classes, per key — the second half of what used to be inline JSX. */
const CELL_CLASS: Record<ContactColumnKey, string | undefined> = {
  name: undefined,
  firstName: 'max-w-[10rem] truncate',
  lastName: 'max-w-[10rem] truncate',
  title: 'max-w-[14rem] truncate',
  company: 'max-w-[14rem]',
  department: 'max-w-[10rem] truncate',
  createdAt: 'whitespace-nowrap text-xs text-fg-muted tabular',
  updatedAt: 'whitespace-nowrap text-xs text-fg-muted tabular',
  actions: undefined,
};

/**
 * The members `/contacts` sorts on — anything else would 422 the `.strict()`
 * schema. ⚠️ *SUPERSEDED 2026-10-09: "The only three the API sorts on"* and
 * `isListSort` over the shared `ListSort`. The page reads `ContactListSort` now
 * (title, company, contactGroup added), and the shared `ListSort` is NOT widened —
 * `/companies` reads it too.
 */
const CONTACT_SORT_KEYS: ReadonlySet<string> = new Set<ContactListSort>([
  'name',
  'createdAt',
  'updatedAt',
  'title',
  'company',
  'contactGroup',
]);

function isContactSort(value: string): value is ContactListSort {
  return CONTACT_SORT_KEYS.has(value);
}

/**
 * The list params that are real server filters on /contacts. Each takes one id
 * or a comma-separated list (owner, 2026-10-28), so both columns are `multi`.
 * The hook hands back a CSV; it is re-validated here id by id, and a malformed
 * member is dropped rather than sent.
 *
 * *SUPERSEDED 2026-10-28: "`?companyId=15,16` and `?contactGroupId=1,2` are
 * both 422s … Neither column may carry `multi`."*
 *
 * ⚠️ **`ContactListParams` holds exactly these two, plus paging, search and
 * sort.** There is no `title`, `createdAt` or `updatedAt` filter to point a
 * column at, and an unknown key is a 422 naming it rather than a param the
 * server quietly drops — so those three columns stay page-scoped.
 *
 * ⚠️ `ownerId` was a member and went on 2026-08-31 with the Owner column.
 * `/contacts` still accepts it; this app stopped asking, because a filter
 * narrowing by a person no column renders would narrow the list with nothing on
 * screen saying so. **That rule is why `contactGroupId` arrives WITH a
 * Department column and could not have arrived without one.**
 */
const SERVER_FILTER_PARAMS = ['companyId', 'contactGroupId'] as const;

type ServerFilterParam = (typeof SERVER_FILTER_PARAMS)[number];

/**
 * The `/contact-groups` master, asked for in one page.
 *
 * `perPage` defaults to 50 and caps at 200 on a reference master, so the
 * ceiling is passed: a master that outgrew the default would silently page and
 * a whole department would vanish from the funnel with nothing looking wrong —
 * which is the exact defect a declared option list exists to remove.
 */
const CONTACT_GROUPS_PER_PAGE = 200;

/**
 * Groups a real timestamp by the LOCAL calendar day the cell already shows.
 * `createdAt` / `updatedAt` are true instants, so localising them is correct
 * here — but slicing the ISO string instead would group by the UTC day and
 * disagree with the rendered cell for anyone west of Greenwich.
 */
function localDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function ContactsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
      <circle cx="9.5" cy="7" r="4" />
      <path d="M17 11h4" />
      <path d="M19 9v4" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

/** Avatar plus the name over the email — the identity cell in both renderings. */
function ContactIdentity({ row, size }: { row: Contact; size: 'sm' | 'md' }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <Avatar name={row.name} size={size} />
      <div className="min-w-0">
        <TextLink to={`/contacts/${row.id}`} className="block truncate text-sm font-medium">
          {row.name}
        </TextLink>
        {/* Emails have no spaces, so only anywhere-wrapping keeps them inside 320px. */}
        {row.email && (
          <p className="text-xs text-fg-muted [overflow-wrap:anywhere]">
            <EmailLink address={row.email} tone="muted" />
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * A contact without a company is a normal record, not a gap in the data:
 * deleting a company sets its contacts' FK to null rather than cascading. A
 * labelled badge says so; a dash would read as missing information.
 */
function CompanyCell({ company }: { company: Contact['company'] }) {
  if (!company)
    return (
      <Badge tone="neutral" variant="outline">
        No account
      </Badge>
    );
  return (
    <TextLink to={`/companies/${company.id}`} className="block truncate text-sm">
      {company.name}
    </TextLink>
  );
}

/**
 * The loading table.
 *
 * ⚠️ **It reads the SAME `visibleColumns` and the same freeze state as the real
 * rows**, which is what it never did before: a skeleton with a different column
 * set is a visible sideways jump the moment the data lands, and a hidden column
 * would have shimmered in a table that was about to render without it.
 */
function TableSkeleton({
  visibleColumns,
  showDelete,
  frozenSideOf,
  frozenOffsets,
}: {
  visibleColumns: readonly ContactColumnKey[];
  showDelete: boolean;
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
}) {
  // A `Record` rather than a switch: `tsc` refuses it when a key is added to
  // COLUMN_KEYS without a shimmer to go under it.
  const cells: Record<ContactColumnKey, ReactNode> = {
    name: (
      <div className="flex items-center gap-2.5">
        <Skeleton className="h-6 w-6 rounded-full" />
        <div className="grid gap-1.5">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-2.5 w-44" />
        </div>
      </div>
    ),
    firstName: <Skeleton className="h-3 w-20" />,
    lastName: <Skeleton className="h-3 w-20" />,
    title: <Skeleton className="h-3 w-24" />,
    company: <Skeleton className="h-3 w-28" />,
    department: <Skeleton className="h-3 w-20" />,
    createdAt: <Skeleton className="h-3 w-24" />,
    updatedAt: <Skeleton className="h-3 w-24" />,
    actions: <Skeleton className={cx('ml-auto h-6', showDelete ? 'w-14' : 'w-6')} />,
  };

  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>
          {visibleColumns.map((key) => {
            const side = frozenSideOf(key);
            return (
              <TD
                key={key}
                numeric={key === ACTIONS_KEY}
                pinned={key === ACTIONS_KEY && side === undefined}
                frozen={side}
                frozenOffset={frozenOffsets[key]}
                className={CELL_CLASS[key]}
              >
                {cells[key]}
              </TD>
            );
          })}
        </TR>
      ))}
    </TBody>
  );
}

/**
 * One contact's row, rendered from `visibleColumns` rather than from a list of
 * its own — see the note on `COLUMN_KEYS`.
 */
function ContactRow({
  row,
  visibleColumns,
  frozenSideOf,
  frozenOffsets,
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
}: {
  row: Contact;
  visibleColumns: readonly ContactColumnKey[];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: (contact: Contact) => void;
  onDelete: (contact: Contact) => void;
}) {
  const cells: Record<ContactColumnKey, ReactNode> = {
    name: <ContactIdentity row={row} size="sm" />,
    firstName: orDash(row.firstName),
    lastName: orDash(row.lastName),
    title: orDash(row.title),
    company: <CompanyCell company={row.company} />,
    // The NAME, rendered; the id is what the funnel sends. A dash for ungrouped
    // — which is the ordinary state for every contact predating the master, not
    // a gap.
    department: orDash(row.contactGroup?.name ?? null),
    createdAt: formatDateTime(row.createdAt),
    updatedAt: formatDateTime(row.updatedAt),
    actions: (
      <div className="flex items-center justify-end gap-1">
        {canUpdate && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            leadingIcon={<PencilIcon />}
            aria-label={`Edit ${row.name}`}
            title={`Edit ${row.name}`}
            onClick={() => onEdit(row)}
          />
        )}
        {canDelete && (
          <Button
            variant="danger"
            size="sm"
            iconOnly
            leadingIcon={<TrashIcon />}
            aria-label={`Delete ${row.name}`}
            title={`Delete ${row.name}`}
            onClick={() => onDelete(row)}
          />
        )}
      </div>
    ),
  };

  return (
    <TR interactive>
      {visibleColumns.map((key) => {
        const side = frozenSideOf(key);
        return (
          <TD
            key={key}
            numeric={key === ACTIONS_KEY}
            pinned={key === ACTIONS_KEY && side === undefined}
            frozen={side}
            frozenOffset={frozenOffsets[key]}
            className={CELL_CLASS[key]}
          >
            {cells[key]}
          </TD>
        );
      })}
    </TR>
  );
}

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li key={i} className="border-b border-border px-4 py-3 last:border-b-0">
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="grid flex-1 gap-1.5">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-2.5 w-40" />
            </div>
          </div>
          <div className="mt-2.5 flex items-center gap-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-20" />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function ContactsPage() {
  const { ability } = useAuth();

  /*
   * The read gate. **This page is where the bug the owner reported actually
   * lived**: a `viewer` on the `operations` set, which carries an explicit
   * `contacts read = none`, could open Contacts — because nothing here asked.
   *
   * ⚠️ **Contacts has NO sidebar row to hide** (it is indexed on `/catalog`), so
   * unlike every other module this page is the ONLY gate there is. A missing row
   * is not a gate.
   *
   * ⚠️ This DEGRADES OPEN. Only an explicit `none` scope denies; an unseeded
   * catalogue, an older API or a failed `/permissions/me` all leave the page
   * visible — see `can()` in `@/lib/permissions`, which carries the reasoning.
   *
   * ⚠️ **Hiding a page is not refusing a request.** `GET /contacts` answers the
   * same to everybody, so nothing here may be worded as though the data were
   * protected. This is a view control.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook. The `canCreate` / `canDelete` gates further down are
   * WRITE gates on a module the API really does enforce, and are untouched.
   */
  const canReadModule = ability.canRead('contacts');

  const [result, setResult] = useState<Paginated<Contact> | null>(null);
  // Remembered on the account (filter memory, owner 2026-10-16). Sort and the
  // funnels are remembered by `useTableFilters`; the page number never is.
  const [search, setSearch] = useRememberedState('search', '');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /*
   * "Show all" — every contact matching the current search, filters and sort,
   * read 100 at a time into one list. Remembered for the account. While it is
   * on `page` is ignored (not reset, so turning it off returns to where the
   * reader was), and `truncated` says the fetch stopped at its cap.
   */
  const [showAll, setShowAll] = useShowAll('contacts');
  const [truncated, setTruncated] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<Contact | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /*
   * Creating and editing a contact takes any write role — the API guards POST
   * and PATCH with `requireRole(...WRITE_ROLES)`. DELETE is the one that stays
   * on the admin tier, and `canDelete` already encodes that.
   */
  const canCreate = ability.canCreate('contacts');
  const canUpdate = ability.canUpdate('contacts');
  const canDelete = ability.canDelete('contacts');
  const rows = useMemo(() => result?.data ?? [], [result]);

  /*
   * The `/contact-groups` master, for the Department funnel.
   *
   * Read once per mount and gated on nothing: reads are open to every
   * authenticated role, and a read-only visitor filtering by department is
   * precisely who this is for.
   *
   * 🔴 **`null` means "not read yet, or the read failed", and it is NOT `[]`.**
   * `ColumnSpec.options` REPLACES the hook's accumulated list outright, so an
   * empty array handed over after a failed request would render "No values on
   * this page" over a table of contacts that mostly DO have a department — a
   * closed set asserted from a request that never came back. `null` hands the
   * column back to the values accumulated from loaded rows, which are narrower
   * than the master but at least come from rows actually on screen. Hence no
   * `failed` flag and no error block: there is nothing a reader could do about
   * it, and the degradation is silent by design rather than by omission.
   *
   * ⚠️ **`isActive: true` is sent, and that is the conservative half of a real
   * trade.** A declared option is a claim that the API accepts that value, and
   * whether the LIST filter tolerates a retired group id is unmeasured — the
   * documented 422 on an inactive id is on the contact WRITE. Offering one and
   * being wrong empties the table, which reads as "no contacts match". The cost
   * is that a contact still holding a since-retired department cannot be
   * filtered for; that was already true when this list was accumulated, so
   * nothing regresses.
   */
  const [contactGroups, setContactGroups] = useState<ContactGroup[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listContactGroups({ perPage: CONTACT_GROUPS_PER_PAGE, isActive: true }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setContactGroups(response.data);
      })
      .catch(() => {
        // Deliberately swallowed — see the block above. `contactGroups` stays
        // null, which is what makes the column degrade instead of lying.
        if (controller.signal.aborted) return;
      });
    return () => controller.abort();
  }, []);

  /*
   * ⚠️ **`undefined` while the master is unread, never `[]`** — the spread at
   * the column below is what turns that into an ABSENT key rather than an empty
   * option list. Rows arrive in the master's own `sort_order, name` and there is
   * no `sort` param, so that order IS the admin's intent and is preserved:
   * `ColumnSpec.options` does not alphabetise, unlike every derived list here.
   */
  const departmentOptions = useMemo<ColumnFilterOption[] | undefined>(
    () =>
      contactGroups?.map((group) => ({ value: String(group.id), label: group.name })) ?? undefined,
    [contactGroups],
  );

  /*
   * Each column declares what the API can really do with it, and the header
   * primitive renders only that. `sortKey` appears on every column /contacts
   * orders by (⚠️ *SUPERSEDED 2026-10-09: "exactly the three columns"* — Title,
   * Account and Department joined); each is pinned with `satisfies
   * ContactListSort`, so the hook's
   * `sort.key` can be handed straight to the request. Account is the only one
   * backed by a query param — everything else is page-scoped, and the popover
   * says so itself rather than pretending to filter 3,000 rows. (Owner was a
   * second server-backed column until 2026-08-31.)
   *
   * Options come from the loaded page, so ids are the stored value and the
   * `format` maps them back to the name that page happens to know.
   */
  const columns = useMemo<ColumnSpec<Contact>[]>(() => {
    const companyNames = new Map<string, string>();
    const groupNames = new Map<string, string>();
    for (const row of rows) {
      if (row.company) companyNames.set(String(row.company.id), row.company.name);
      if (row.contactGroup) groupNames.set(String(row.contactGroup.id), row.contactGroup.name);
    }
    return [
      {
        key: 'name',
        label: CONTACT_NAME_LABELS.name,
        get: (row) => row.name,
        sortKey: 'name' satisfies ContactListSort,
      },
      {
        key: 'firstName',
        label: CONTACT_NAME_LABELS.firstName,
        get: (row) => row.firstName,
      },
      {
        key: 'lastName',
        label: CONTACT_NAME_LABELS.lastName,
        get: (row) => row.lastName,
      },
      {
        key: 'title',
        label: 'Title',
        get: (row) => row.title,
        sortKey: 'title' satisfies ContactListSort,
      },
      {
        key: 'company',
        label: 'Account',
        get: (row) => row.company?.id ?? null,
        format: (value) => companyNames.get(value) ?? `Account #${value}`,
        scope: 'server',
        param: 'companyId',
        multi: true,
        // Ordered by the company's NAME server-side, never `company_id`.
        sortKey: 'company' satisfies ContactListSort,
        /*
         * ⚠️ **NO `options`, and that is a DECISION rather than the defect
         * `ColumnSpec.options` was added to fix.** Reviewed 2026-09-04; do not
         * "finish" this by declaring a list.
         *
         * The accumulated behaviour is genuinely narrow here — ten contacts a
         * page against ~150 accounts, so the funnel offers a handful — and the
         * obvious repair is to read `/companies` and declare them. It was
         * weighed and refused:
         *
         *  - **No list could be complete.** `/companies` caps `perPage` at 100
         *    and there are more accounts than that, so a declared list is a
         *    SLICE — and which slice is decided by the endpoint's `createdAt
         *    desc` default, i.e. an arbitrary one.
         *  - **A truncated closed list is worse than an honest short one**,
         *    because it looks authoritative. `ColumnFilterPanel`'s own search
         *    box searches only the options it was handed, so an account past
         *    the cap would answer "No values match" — the same failure this
         *    control exists to remove, wearing a directory's clothes.
         *  - The hook's own doc draws exactly this line: a declared list is for
         *    a CLOSED vocabulary the API defines, and "tolerable for an open set
         *    like companies or users, where no list could ever be complete".
         *
         * What reaches an account whose contacts are not on this page is the
         * panel's search box, which is a real server-side `?search=`, and
         * `/companies/:id`, which lists its own contacts. If this column is ever
         * to offer the directory it needs a server-backed typeahead in the
         * popover, not a first page dressed as a vocabulary.
         *
         * (The Owner column was a second server-scoped column until 2026-08-31
         * and is gone — see `SERVER_FILTER_PARAMS`. Department below is the
         * other one, and the contrast with this column is the whole point: a
         * closed master of a handful of rows can be declared honestly, an open
         * directory of hundreds cannot.)
         */
      },
      /**
       * Department — the one column on this page whose filter really does reach
       * every contact, because its vocabulary is small, closed and fetchable.
       *
       * ⚠️ **`options` come from the `/contact-groups` MASTER, not from the
       * loaded page**, and that is the whole reason the column is worth having.
       * Accumulated, the funnel would offer whichever departments ten rows
       * happened to carry — and once one was picked the next response contained
       * that department alone, so the list could not grow back and the filter was
       * one-way. Unlike Account above, the master fits in a single request under
       * its own `perPage` ceiling, so the declared list is genuinely COMPLETE
       * rather than an arbitrary first page dressed as a vocabulary.
       *
       * ⚠️ **Spread rather than assigned.** `options: undefined` and an absent
       * key are the same thing to the hook, but writing the key unconditionally
       * invites the next reader to "fix" it into `[]` — which REPLACES the
       * accumulated list with nothing and reads as "no departments exist".
       *
       * ⚠️ **`get` returns the id, matching the option values**, so the
       * emptiness toggle reads "has no department" correctly. `null` there is the
       * ORDINARY state: every contact predating the master is ungrouped, and
       * nothing has classified them.
       *
       * ⚠️ **`format` maps an id back to a name from the loaded rows** and is
       * reached only when the master read failed and the hook fell back to its
       * accumulated values. It is never a NAME match — `contactGroup.name` is
       * admin-editable free text and nothing here decides anything by it; the id
       * is what is sent and `slug` is what a rule would key on.
       *
       * `multi` since 2026-10-28 — `?contactGroupId=1,2` is accepted (see
       * `SERVER_FILTER_PARAMS`). ⚠️ *SUPERSEDED 2026-10-09: "And no
       * `sortKey`: `ListSort` is name | createdAt | updatedAt, so a fourth member
       * would be a 422 that blanks the table rather than an unsorted column."*
       * `/contacts` takes `contactGroup` now (ordered by the group's NAME), read
       * through the page's own `ContactListSort` rather than a widened `ListSort`.
       */
      {
        key: 'department',
        label: 'Department',
        get: (row) => row.contactGroup?.id ?? null,
        format: (value) => groupNames.get(value) ?? `Department #${value}`,
        scope: 'server',
        param: 'contactGroupId',
        multi: true,
        sortKey: 'contactGroup' satisfies ContactListSort,
        ...(departmentOptions ? { options: departmentOptions } : {}),
      },
      {
        key: 'createdAt',
        label: 'Created',
        get: (row) => localDay(row.createdAt),
        sortKey: 'createdAt' satisfies ContactListSort,
      },
      {
        key: 'updatedAt',
        label: 'Updated',
        get: (row) => localDay(row.updatedAt),
        sortKey: 'updatedAt' satisfies ContactListSort,
      },
    ];
  }, [rows, departmentOptions]);

  // Derived, never hand-listed: a column added above cannot drift out of the
  // mobile sheet the way a parallel array would.
  const filterKeys = useMemo(() => columns.map((column) => column.key), [columns]);

  /*
   * Column layout — order, hidden set and the two frozen edges — stored against
   * the ACCOUNT rather than this browser. `useColumnLayout` carries the
   * reasoning; the two rules worth repeating at the call site are that the
   * HIDDEN keys are what is stored (so a column this table gains later is
   * visible to everybody, never silently subtracted) and that hiding the last
   * visible column is refused rather than obeyed.
   *
   * ⚠️ **It degrades to the declared layout and never blocks the table.** A
   * failed preferences read leaves `EMPTY_LAYOUT`, which renders exactly the
   * seven columns below in exactly this order — the rows are the page, the
   * arrangement is chrome.
   */
  const layout = useColumnLayout(COLUMN_LAYOUT_SCOPE, COLUMN_KEYS, LOCKED_KEYS, PINNED_KEYS);

  /*
   * The controller hands back `readonly string[]`; everything downstream indexes
   * `COLUMN_META` and `CELL_CLASS` by key, so it is narrowed once here rather
   * than asserted at each of the three renderings.
   */
  const visibleColumns = useMemo(
    () => layout.visibleKeys.filter(isColumnKey),
    [layout.visibleKeys],
  );

  /*
   * ⚠️ **The offsets must count Actions, because `pinned` IS the right edge.**
   * `useFrozenOffsets` walks only the columns its side map calls frozen, so a
   * map that left Actions out would hand the next right-frozen column an offset
   * of 0 and park it directly underneath the lane — two opaque cells on one
   * edge, nothing erroring, one column simply gone.
   *
   * ✅ **`PINNED_KEYS` is how that is said now.** *SUPERSEDED: a local
   * `measuredFrozenSideOf` `useCallback` resolving Actions to `'right'` with
   * `?? 'right'`, and the clause justifying the `??` — "a reader who freezes
   * Actions explicitly gets that side instead … the manager renders a freeze
   * control for every column including the locked one, and a control whose only
   * outcome is nothing is worse than no control."* **The manager no longer
   * renders those controls for a pinned key**, so there is no explicit freeze to
   * defer to and nothing left for the wrapper to decide. `useFrozenOffsets`
   * takes the pinned keys itself and resolves them to the right edge before it
   * consults the side map.
   *
   * `layout.frozenSideOf` is the controller's own `useCallback` and is stable
   * across renders, so it is handed over directly — passing `layout` would
   * re-subscribe the offsets' `ResizeObserver` on every keystroke in the search
   * box.
   */
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    visibleColumns,
    layout.frozenSideOf,
    PINNED_KEYS,
  );

  /*
   * ⛔ **The table's floor is a NUMBER, applied inline.** It was `min-w-[1004px]`
   * in two places; a hidden column has to take its width with it, and ⚠️
   * **Tailwind v4 scans source text, so `min-w-[${n}px]` emits no rule at all** —
   * the class lands in the DOM and does nothing, which looks exactly like a
   * table that chose not to be wide. `UniversityPartnershipsPage` and `Table`'s
   * own freezing note both record the same trap.
   */
  const tableMinWidth = useMemo(
    () => visibleColumns.reduce((sum, key) => sum + COLUMN_META[key].minWidth, 0),
    [visibleColumns],
  );

  /*
   * The manager's labels come from the column specs above, so a heading renamed
   * there cannot end up reading one thing on the table and another in the list
   * of columns. Actions has no spec — it carries no filter and no sort — so it
   * is the one key named here.
   */
  const labelOf = useCallback(
    (key: string): string =>
      key === ACTIONS_KEY
        ? ACTIONS_LABEL
        : (columns.find((column) => column.key === key)?.label ?? key),
    [columns],
  );

  const filters = useTableFilters(rows, columns);
  const visibleRows = filters.visibleRows;
  const hiddenByFilters = rows.length - visibleRows.length;

  // `serverParams` keeps a stable identity while its contents are unchanged, so
  // it is safe both as a memo dep and, through `listFilters`, as a fetch dep.
  const { serverParams } = filters;
  const listFilters = useMemo(() => {
    const next: Partial<Record<ServerFilterParam, string>> = {};
    for (const param of SERVER_FILTER_PARAMS) {
      const ids = (serverParams[param] ?? '')
        .split(',')
        .map((part) => Number(part.trim()))
        .filter((id) => Number.isInteger(id) && id > 0);
      if (ids.length > 0) next[param] = ids.join(',');
    }
    return next;
  }, [serverParams]);

  const activeSort = filters.sort;
  const sort: ContactListSort =
    activeSort && isContactSort(activeSort.key) ? activeSort.key : 'createdAt';
  const order: ListOrder = activeSort?.direction ?? 'desc';

  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : showAll ? 1 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      const query = { sort, order, ...listFilters, ...(search ? { search } : {}) };
      // `fetchAllPages` needs a signal; a save-triggered reload passes none.
      const request: Promise<Paginated<Contact>> = showAll
        ? fetchAllPages<Contact>(
            (pageNo, perPage, pageSignal) =>
              api.listContacts({ ...query, page: pageNo, perPage }, pageSignal),
            signal ?? new AbortController().signal,
          ).then((all) => {
            setTruncated(all.truncated);
            return {
              data: all.rows,
              meta: { page: 1, perPage: all.rows.length, total: all.total, totalPages: 1 },
            };
          })
        : api.listContacts({ ...query, page, perPage: PER_PAGE }, signal).then((data) => {
            setTruncated(false);
            return data;
          });
      return request
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load contacts');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, search, sort, order, listFilters, showAll],
  );

  // Debounce so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  /*
   * The hook's headers, with the page reset folded into the events that change
   * the query. Doing it here rather than in an effect watching `sort` means no
   * request is ever fired for page 3 of a result set that no longer exists.
   *
   * A page-scoped column deliberately does NOT reset: it filters the rows
   * already loaded, so jumping to page 1 would throw away the very rows the
   * user was filtering. Only a server-scoped change re-queries.
   *
   * Not memoised on purpose — the hook returns a fresh object each render, so a
   * `useCallback` here would hold nothing. It is only ever called during render.
   */
  function headerFor(key: string): FilterableTHProps {
    const props = filters.headerFor(key);
    const filter = props.filter;
    if (!filter) return props;
    const sortControl = filter.sort;
    return {
      ...props,
      filter: {
        ...filter,
        onChange: (next) => {
          filter.onChange(next);
          if (filter.scope === 'server') setPage(1);
        },
        ...(sortControl
          ? {
              sort: {
                direction: sortControl.direction,
                onSort: (direction) => {
                  sortControl.onSort(direction);
                  setPage(1);
                },
              },
            }
          : {}),
      },
    };
  }

  function openCreate() {
    setEditing(undefined);
    setFormOpen(true);
  }

  function openEdit(contact: Contact) {
    setEditing(contact);
    setFormOpen(true);
  }

  function requestDelete(contact: Contact) {
    setDeleteError(null);
    setPendingDelete(contact);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteContact(pendingDelete.id);
      setPendingDelete(null);
      // Removing the last row of a page would otherwise strand the user on an
      // empty final page; stepping back re-fetches through the page effect.
      if (!showAll && rows.length === 1 && page > 1) setPage((p) => p - 1);
      else await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 403) {
        // A 403 body has no `details`, so it renders as a banner rather than
        // per-field. `denialReason` is null when the ability still thought the
        // delete was allowed — a role changed under the live token.
        setDeleteError(
          denialReason(ability, 'delete', 'contacts') ??
            'You no longer have permission to delete contacts.',
        );
      } else {
        setDeleteError(caught instanceof Error ? caught.message : 'Delete failed');
      }
    } finally {
      setDeleting(false);
    }
  }

  function clearSearch() {
    setSearch('');
    setPage(1);
  }

  /*
   * `countLabel` is GONE — owner's stricter helper-text ruling, 2026-09-05. It
   * read "Loading…" or "{n} contacts matching “{term}”", and fed the panel's
   * `description` plus the live region under it. Same removal as its twin on
   * `/companies`.
   *
   * ⚠️ **No count is lost from the screen**: the table footer still prints
   * "Showing {first}–{last} of {total}" and the page count, from the pager's own
   * values rather than from this sentence.
   */

  const emptyState = (
    <EmptyStateSlot
      search={search}
      onClear={clearSearch}
      onCreate={openCreate}
      canCreate={canCreate}
      hasError={Boolean(error)}
      columnFiltered={rows.length > 0}
      onClearFilters={filters.clearAll}
    />
  );

  /*
   * ⚠️ Below every hook, so the early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard —
   * every signed-in route in this app is an ordinary `Shell` route, so a reader
   * arriving by URL, from a link in an email, or from the command palette must
   * get a sentence rather than an error or a blank screen.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Records" title="Contacts" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Records"
        title="Contacts"
        actions={
          canCreate ? (
            <Button variant="primary" leadingIcon={<PlusIcon />} onClick={openCreate}>
              New contact
            </Button>
          ) : null
        }
      />

      <Panel>
        <PanelHeader
          title="All contacts"
          actions={
            <>
              <div className="w-full sm:w-64">
                <Field label="Search contacts" hideLabel>
                  {({ id }) => (
                    <Input
                      id={id}
                      type="search"
                      placeholder="Search by name, email or title…"
                      leadingIcon={<SearchIcon />}
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                  )}
                </Field>
              </div>
              {/* ⚠️ **Hidden below `md`, where there is no table to arrange** —
                  the rows are a stacked card list there and the manager would be
                  a control with nothing to act on. It is wrapped rather than
                  given a `hidden md:inline-flex` of its own: `hidden` and the
                  Button's own `display` are the same CSS property, so which wins
                  would be decided by the emitted stylesheet's order rather than
                  by the order they are written — the trap `TH.wrap` and
                  `TD.verticalAlign` both exist to avoid. */}
              <div className="hidden md:block">
                <ColumnManager
                  layout={layout}
                  labelOf={labelOf}
                  lockedKeys={LOCKED_KEYS}
                  lockReasonOf={lockReasonOf}
                  pinnedKeys={PINNED_KEYS}
                />
              </div>
            </>
          }
        />

        {/* The live region mirrored `countLabel` and went with it — with nothing
            to announce it would have been an empty region. */}

        {filters.activeCount > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border bg-surface-sunken px-4 py-2">
            <p className="m-0 text-xs text-fg-secondary">
              {pluralise(filters.activeCount, 'column filter')} applied.{' '}
              {hiddenByFilters > 0
                ? `${hiddenByFilters} of the ${pluralise(rows.length, 'row')} loaded here are hidden.`
                : 'Every loaded row still matches.'}
            </p>
            <Button variant="secondary" size="sm" onClick={filters.clearAll}>
              Clear column filters
            </Button>
          </div>
        )}

        {error && (
          <div className="border-b border-border px-4 py-2">
            <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load contacts.</>}>
              <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
            </Notice>
          </div>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {/* Below md a table cannot fit 320px, so rows become a stacked list. */}
          <div className="md:hidden">
            {/* The list has no headers, so the funnels have nowhere to live
                here. Same `headerFor` and `clearAll` as the desktop table — one
                filter store, so a filter set on a phone is still set on a
                laptop. */}
            <div className="border-b border-border px-4 py-2">
              <FilterSheet
                keys={filterKeys}
                headerFor={headerFor}
                activeCount={filters.activeCount}
                clearAll={filters.clearAll}
              />
            </div>
            {initialLoad ? (
              <ListSkeleton />
            ) : visibleRows.length === 0 ? (
              emptyState
            ) : (
              <ul className="m-0 list-none p-0">
                {visibleRows.map((row) => (
                  <li key={row.id} className="border-b border-border px-4 py-3 last:border-b-0">
                    <ContactIdentity row={row} size="md" />
                    <dl className="mt-2.5 grid gap-1.5 text-xs">
                      {/* The name parts, matching the desktop table's First
                          and Last name columns. Omitted when unset, as Title is. */}
                      {row.firstName && (
                        <div className="flex items-baseline gap-2">
                          <dt className="w-20 shrink-0 text-fg-subtle">
                            {CONTACT_NAME_LABELS.firstName}
                          </dt>
                          <dd className="m-0 min-w-0 flex-1 text-fg-secondary">{row.firstName}</dd>
                        </div>
                      )}
                      {row.lastName && (
                        <div className="flex items-baseline gap-2">
                          <dt className="w-20 shrink-0 text-fg-subtle">
                            {CONTACT_NAME_LABELS.lastName}
                          </dt>
                          <dd className="m-0 min-w-0 flex-1 text-fg-secondary">{row.lastName}</dd>
                        </div>
                      )}
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Account</dt>
                        <dd className="m-0 min-w-0 flex-1 text-fg-secondary">
                          <CompanyCell company={row.company} />
                        </dd>
                      </div>
                      {/* Must match the desktop table's Department column — the
                          two renderings cannot disagree about a contact, and a
                          value reachable only above a breakpoint is
                          indistinguishable from one that was removed. Omitted
                          when unset, exactly as Title is. */}
                      {row.contactGroup && (
                        <div className="flex items-baseline gap-2">
                          <dt className="w-20 shrink-0 text-fg-subtle">Dept</dt>
                          <dd className="m-0 min-w-0 flex-1 text-fg-secondary">
                            {row.contactGroup.name}
                          </dd>
                        </div>
                      )}
                      {row.title && (
                        <div className="flex items-baseline gap-2">
                          <dt className="w-20 shrink-0 text-fg-subtle">Title</dt>
                          <dd className="m-0 min-w-0 flex-1 text-fg-secondary">{row.title}</dd>
                        </div>
                      )}
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Updated</dt>
                        <dd className="m-0 min-w-0 flex-1 text-fg-muted tabular">
                          {formatDateTime(row.updatedAt)}
                        </dd>
                      </div>
                    </dl>
                    <div className="mt-3 flex items-center gap-2">
                      {canUpdate && (
                        <Button
                          variant="secondary"
                          size="sm"
                          leadingIcon={<PencilIcon />}
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="danger"
                          size="sm"
                          leadingIcon={<TrashIcon />}
                          onClick={() => requestDelete(row)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="hidden md:block">
            {/* `frozenHeader` on BOTH wrappers — skeleton and rows — so the
                scroll area does not change shape when the data lands. */}
            {initialLoad ? (
              <TableWrap frozenHeader>
                {/* ✅ **The floor sits on `Table` itself.** *SUPERSEDED: "**The
                    floor sits on a wrapper, not on `Table`**, which takes only
                    `className`."* `Table` takes a `style` as of 2026-09-12, so
                    the spacer `<div>` is gone. The value is computed, which is
                    why it has to reach the DOM as a style at all: Tailwind v4
                    scans source text and `min-w-[${n}px]` emits no rule. */}
                <Table style={{ minWidth: tableMinWidth }}>
                  <ContactsTableHead
                    headRowRef={headRowRef}
                    visibleColumns={visibleColumns}
                    headerFor={headerFor}
                    frozenSideOf={layout.frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                  <TableSkeleton
                    visibleColumns={visibleColumns}
                    showDelete={canDelete}
                    frozenSideOf={layout.frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                </Table>
              </TableWrap>
            ) : visibleRows.length === 0 ? (
              emptyState
            ) : (
              <TableWrap frozenHeader>
                {/* Same floor as the skeleton above, and the same expression —
                    not a second copy of a hand-summed one. */}
                <Table style={{ minWidth: tableMinWidth }}>
                  <ContactsTableHead
                    headRowRef={headRowRef}
                    visibleColumns={visibleColumns}
                    headerFor={headerFor}
                    frozenSideOf={layout.frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                  <TBody>
                    {visibleRows.map((row) => (
                      <ContactRow
                        key={row.id}
                        row={row}
                        visibleColumns={visibleColumns}
                        frozenSideOf={layout.frozenSideOf}
                        frozenOffsets={frozenOffsets}
                        canUpdate={canUpdate}
                        canDelete={canDelete}
                        onEdit={openEdit}
                        onDelete={requestDelete}
                      />
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            )}
          </div>
        </div>

        {result && (
          // `ListPager` IS the PanelFooter. Column filters run over the loaded
          // rows, so the computed range would overstate what is on screen —
          // that case supplies its own line.
            <ListPager
              {...(total > 0 && hiddenByFilters > 0
                ? {
                    summary: `Showing ${visibleRows.length} of rows ${firstRow}–${lastRow} · ${total} in total`,
                  }
                : {})}
              page={showAll ? 1 : page}
              totalPages={showAll ? 1 : totalPages}
              total={total}
              firstRow={firstRow}
              lastRow={lastRow}
              perPage={PER_PAGE}
              showAll={showAll}
              loading={loading}
              onPage={setPage}
              onToggleShowAll={setShowAll}
              noun="contacts"
              truncated={showAll && truncated}
            />
        )}
      </Panel>

      <ContactFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        {...(editing ? { contact: editing } : {})}
        onSaved={() => {
          setFormOpen(false);
          void load();
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : 'Delete contact?'}
        description={
          <>
            This removes {pendingDelete?.name ?? 'this contact'}
            {pendingDelete?.company ? ` from ${pendingDelete.company.name}` : ''} and everything
            recorded against them. It cannot be undone.
          </>
        }
        confirmLabel="Delete contact"
        loading={deleting}
        {...(deleteError ? { error: deleteError } : {})}
      />
    </>
  );
}

/**
 * One mechanism for sorting and filtering, not two: `FilterableTH` carries the
 * funnel, and the sort rows inside it appear only for the columns whose spec
 * has a `sortKey`. Actions has no filter, so it renders as a plain header.
 *
 * ⚠️ **Actions is still PINNED at its default, and the reason is measured.**
 * This table is ~1004px wide against a content area of the viewport minus the
 * ~256px sidebar, so unpinned the column straddled the edge from 1280 down —
 * measured on production at 1280, where Edit was still reachable and Delete,
 * 36px further right, was not. A half-clipped column is the easy one to miss,
 * because the cell looks present. It used to be pinned in THREE hand-written
 * places (this header, the body cell and the skeleton); all three now read
 * `COLUMN_KEYS`, so the skeleton cannot fall out of step and jump sideways when
 * the data lands.
 *
 * ✅ **The row is `TR`, and it carries the ref.** *SUPERSEDED: "**The row is a
 * plain `<tr>`, not `TR`** … `TR` is not a `forwardRef`, so there is no way to
 * hand the hook the element through it. The classes below are exactly what `TR`
 * emits for a non-interactive row."* `TR` takes a `ref` as of 2026-09-12, so
 * the copied class string is gone — it was a second copy of another component's
 * styling with nothing to keep it in step, and `TR` had already grown a
 * `group/rowhover` branch that this copy did not have. `useFrozenOffsets` still
 * measures the rendered width of each header cell, which is the only place
 * those widths exist on an auto-layout table with no `<colgroup>`.
 */
function ContactsTableHead({
  headRowRef,
  visibleColumns,
  headerFor,
  frozenSideOf,
  frozenOffsets,
}: {
  headRowRef: RefObject<HTMLTableRowElement | null>;
  visibleColumns: readonly ContactColumnKey[];
  headerFor: TableFilters<Contact>['headerFor'];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
}) {
  return (
    <THead>
      <TR ref={headRowRef}>
        {visibleColumns.map((key) => {
          const side = frozenSideOf(key);
          const shared = {
            className: COLUMN_META[key].headClass,
            numeric: key === ACTIONS_KEY,
            pinned: key === ACTIONS_KEY && side === undefined,
            frozen: side,
            frozenOffset: frozenOffsets[key],
          };
          const props: FilterableTHProps =
            key === ACTIONS_KEY
              ? { label: ACTIONS_LABEL, ...shared }
              : { ...headerFor(key), ...shared };
          return <FilterableTH key={key} {...props} />;
        })}
      </TR>
    </THead>
  );
}

function EmptyStateSlot({
  search,
  onClear,
  onCreate,
  canCreate,
  hasError,
  columnFiltered,
  onClearFilters,
}: {
  search: string;
  onClear: () => void;
  onCreate: () => void;
  /** Hides the call to action for a role that cannot create a contact. */
  canCreate: boolean;
  hasError: boolean;
  /** True when the page did load rows and the column filters hid all of them. */
  columnFiltered: boolean;
  onClearFilters: () => void;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<ContactsIcon />}
        title="Nothing to show"
        description="Contacts couldn’t be loaded. Try again."
      />
    );
  }

  if (columnFiltered) {
    return (
      <EmptyState
        icon={<ContactsIcon />}
        title="No rows on this page match the column filters"
        description="These filters cover this page only — a match may sit on another. Search to narrow the whole list."
        action={
          <Button variant="secondary" size="sm" onClick={onClearFilters}>
            Clear column filters
          </Button>
        }
      />
    );
  }

  return (
    <EmptyState
      icon={<ContactsIcon />}
      title={search ? 'No contacts match that search' : 'No contacts yet'}
      description={
        search
          ? 'Try a different name, email or job title.'
          : 'Add the people you work with at each partner account.'
      }
      action={
        search ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear search
          </Button>
        ) : canCreate ? (
          <Button variant="secondary" size="sm" leadingIcon={<PlusIcon />} onClick={onCreate}>
            New contact
          </Button>
        ) : null
      }
    />
  );
}
